import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, CircleNotch } from "@phosphor-icons/react";

const LOGO = "/novuspak-logo.png";
const BG =
  "https://static.prod-images.emergentagent.com/jobs/aff6d1cc-f774-4baa-b558-a03dcf5b8dd4/images/378f7ded066c0c07a23b882ee8611fe8585fd648a83f991b9a1d7e7b71f50c81.png";

export default function Register() {
  const { loginSuccess } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      loginSuccess(data);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-[#F8F9FA]">
        <div className="w-full max-w-md animate-fade-up">
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO} alt="NovusPak" className="h-12 w-12 object-contain shrink-0" />
            <span className="font-heading font-extrabold text-xl text-[#0F172A] leading-tight">
              Control de Inventario
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">
            Crea tu cuenta
          </h1>
          <p className="text-[#475569] mt-2 mb-8">Empieza a controlar tu inventario en minutos.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">
                Nombre
              </Label>
              <Input
                id="name"
                data-testid="register-name-input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Tu nombre"
                className="h-11 bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">
                Email
              </Label>
              <Input
                id="email"
                data-testid="register-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tucorreo@empresa.com"
                className="h-11 bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">
                Contraseña
              </Label>
              <Input
                id="password"
                data-testid="register-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className="h-11 bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
              />
            </div>

            {error && (
              <p data-testid="register-error" className="text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid="register-submit-button"
              disabled={loading}
              className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold"
            >
              {loading ? <CircleNotch size={18} className="animate-spin" /> : "Crear cuenta"}
            </Button>
          </form>

          <p className="text-sm text-[#475569] mt-6">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" data-testid="goto-login" className="font-semibold text-[#0F172A] underline underline-offset-4">
              Inicia sesión
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0F172A]/70" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <Package size={48} weight="duotone" className="mb-6 opacity-90" />
          <h2 className="font-heading text-4xl font-black tracking-tight max-w-md leading-tight">
            Todo tu inventario, en un solo lugar.
          </h2>
          <p className="mt-4 text-white/80 max-w-md">
            Lotes, movimientos, alertas y rankings de rotación con sugerencias de liquidación.
          </p>
        </div>
      </div>
    </div>
  );
}
